import { Link } from 'react-router';
import { format, parseISO } from 'date-fns';
import { Club } from '@repo/types';
import { Image, ImageVariant } from '../../../../common/components/Image';

type ClubInfoProps = {
  club: Club;
};

export function ClubInfo({ club }: ClubInfoProps) {
  const foundedDate = club.foundedAt ? format(parseISO(club.foundedAt), 'yyyy') : null;

  return (
    <div className="team-overview-general-info__wrapper">
      <h3 className="team-overview__tab-title">About {club.nickName ?? club.fullName}</h3>
      <div className="team-overview-general-info__container">
        <div>
          <ul className="team-overview-list">
            {club.nickName && (
              <li className="team-overview-list__item">
                <span className="team-overview-list__type">Nickname</span>
                <span className="team-overview-list__value">{club.nickName}</span>
              </li>
            )}
            {foundedDate && (
              <li className="team-overview-list__item">
                <span className="team-overview-list__type">Founded</span>
                <span className="team-overview-list__value">{foundedDate}</span>
              </li>
            )}
          </ul>
          {club.clubHistory && (
            <div
              className="team-overview-general-info__history"
              dangerouslySetInnerHTML={{ __html: club.clubHistory }}
            />
          )}
        </div>
        <div className="team-overview-general-info__right-content-container">
          <ul className="team-overview-list">
            {club.clubWebsite && (
              <li className="team-overview-list__item">
                <span className="team-overview-list__type">Website</span>
                <Link to={club.clubWebsite} target="_blank">
                  {club.clubWebsite}
                </Link>
              </li>
            )}
            {club.clubEmail && (
              <li className="team-overview-list__item">
                <span className="team-overview-list__type">Email</span>
                <Link to={`mailto:${club.clubEmail}`}>{club.clubEmail}</Link>
              </li>
            )}
            {club.clubPhone && (
              <li className="team-overview-list__item">
                <span className="team-overview-list__type">Telephone</span>
                <Link to={`tel:${club.clubPhone}`}>{club.clubPhone}</Link>
              </li>
            )}
            {club.clubShopWebsite && (
              <li className="team-overview-list__item">
                <span className="team-overview-list__type">Club shop</span>
                <Link to={club.clubShopWebsite} target="_blank">
                  {club.clubShopWebsite}
                </Link>
              </li>
            )}
            {club.clubProgramWebsite && (
              <li className="team-overview-list__item">
                <span className="team-overview-list__type">program</span>
                <Link to={club.clubProgramWebsite} target="_blank">
                  {club.clubProgramWebsite}
                </Link>
              </li>
            )}
            {club.facebook && (
              <li className="team-overview-list__item">
                <span className="team-overview-list__type">Facebook</span>
                <Link to={club.facebook} target="_blank">
                  {club.facebook}
                </Link>
              </li>
            )}
            {club.twitter && (
              <li className="team-overview-list__item">
                <span className="team-overview-list__type">X (TWITTER)</span>
                <Link to={club.twitter} target="_blank">
                  {club.twitter}
                </Link>
              </li>
            )}
            {club.youtube && (
              <li className="team-overview-list__item">
                <span className="team-overview-list__type">Youtube</span>
                <Link to={club.youtube} target="_blank">
                  {club.youtube}
                </Link>
              </li>
            )}
            {club.clubTicketWebsite && (
              <li className="team-overview-list__item">
                <span className="team-overview-list__type">Tickets</span>
                <Link to={club.clubTicketWebsite} target="_blank">
                  {club.clubTicketWebsite}
                </Link>
              </li>
            )}
          </ul>
        </div>
      </div>
      <div className="team-overview-general-info__kits-container">
        {club.homeKit && (
          <div className="team-overview-general-info__kit-wrapper">
            <h4 className="team-overview-general-info__kit-type">Home Kit</h4>
            <div className="team-overview-general-info__kit-container">
              <Image
                variant={ImageVariant.SquareLogoLarge}
                src={club.homeKit}
                alt={`${club.shortName} home kit`}
                width={200}
                height={230}
              />
              <ul role={'list'} className="team-overview-general-info__kit-colors-list">
                <li className="team-overview-general-info__kit-colors-list-item">
                  <div
                    className={'team-overview-general-info__kit-color-box'}
                    style={{ backgroundColor: club.homeKitOutfieldShirtColor }}
                  />
                </li>
                <li className="team-overview-general-info__kit-colors-list-item">
                  <div
                    className={'team-overview-general-info__kit-color-box'}
                    style={{ backgroundColor: club.homeKitShortsColor }}
                  />
                </li>
                <li className="team-overview-general-info__kit-colors-list-item">
                  <div
                    className={'team-overview-general-info__kit-color-box'}
                    style={{ backgroundColor: club.homeKitSocksColor }}
                  />
                </li>
                <li className="team-overview-general-info__kit-colors-list-item">
                  <div
                    className={'team-overview-general-info__kit-color-box'}
                    style={{ backgroundColor: club.homeKitKeeperShirtColor }}
                  />
                </li>
              </ul>
            </div>
          </div>
        )}
        {club.awayKit && (
          <div className="team-overview-general-info__kit-wrapper">
            <h4 className="team-overview-general-info__kit-type">Away Kit</h4>
            <div className="team-overview-general-info__kit-container">
              <Image
                variant={ImageVariant.SquareLogoLarge}
                src={club.awayKit}
                alt={`${club.shortName} home kit`}
                width={200}
                height={230}
              />
              <ul role={'list'} className="team-overview-general-info__kit-colors-list">
                <li className="team-overview-general-info__kit-colors-list-item">
                  <div
                    className={'team-overview-general-info__kit-color-box'}
                    style={{ backgroundColor: club.awayKitOutfieldShirtColor }}
                  />
                </li>
                <li className="team-overview-general-info__kit-colors-list-item">
                  <div
                    className={'team-overview-general-info__kit-color-box'}
                    style={{ backgroundColor: club.awayKitShortsColor }}
                  />
                </li>
                <li className="team-overview-general-info__kit-colors-list-item">
                  <div
                    className={'team-overview-general-info__kit-color-box'}
                    style={{ backgroundColor: club.awayKitSocksColor }}
                  />
                </li>
                <li className="team-overview-general-info__kit-colors-list-item">
                  <div
                    className={'team-overview-general-info__kit-color-box'}
                    style={{ backgroundColor: club.awayKitKeeperShirtColor }}
                  />
                </li>
              </ul>
            </div>
          </div>
        )}
        {club.thirdKit && (
          <div className="team-overview-general-info__kit-wrapper">
            <h4 className="team-overview-general-info__kit-type">Third Kit</h4>
            <div className="team-overview-general-info__kit-container">
              <Image
                variant={ImageVariant.SquareLogoLarge}
                src={club.thirdKit}
                alt={`${club.shortName} home kit`}
                width={200}
                height={230}
              />
              <ul role={'list'} className="team-overview-general-info__kit-colors-list">
                <li className="team-overview-general-info__kit-colors-list-item">
                  <div
                    className={'team-overview-general-info__kit-color-box'}
                    style={{ backgroundColor: club.thirdKitOutfieldShirtColor }}
                  />
                </li>
                <li className="team-overview-general-info__kit-colors-list-item">
                  <div
                    className={'team-overview-general-info__kit-color-box'}
                    style={{ backgroundColor: club.thirdKitShortsColor }}
                  />
                </li>
                <li className="team-overview-general-info__kit-colors-list-item">
                  <div
                    className={'team-overview-general-info__kit-color-box'}
                    style={{ backgroundColor: club.thirdKitSocksColor }}
                  />
                </li>
                <li className="team-overview-general-info__kit-colors-list-item">
                  <div
                    className={'team-overview-general-info__kit-color-box'}
                    style={{ backgroundColor: club.thirdKitKeeperShirtColor }}
                  />
                </li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
